'use client';

import React, { useEffect, useRef } from 'react';

type CompetitionHtmlEmbedProps = {
    html: string;
};

/**
 * Renders the operator-supplied embed snippet of the contest section.
 *
 * The markdown fields already pass raw HTML through `rehypeRaw`, but React never runs a `<script>`
 * it renders, and embed providers rely on exactly that script: Tally ships an `<iframe>` carrying
 * only `data-tally-src` plus a loader that fills in the real `src` and keeps the iframe's height in
 * sync with the form. Assigning `innerHTML` does not execute scripts either, so each one is
 * re-created as a fresh element — that is what actually schedules it.
 *
 * The snippet is written straight into the DOM rather than through React, so React never owns these
 * nodes and cannot fight the loader over them.
 */
const CompetitionHtmlEmbed: React.FC<CompetitionHtmlEmbedProps> = ({ html }) => {
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) {
            return;
        }

        container.innerHTML = html;

        container.querySelectorAll('script').forEach((inertScript) => {
            const script = document.createElement('script');
            for (const { name, value } of inertScript.attributes) {
                script.setAttribute(name, value);
            }
            script.text = inertScript.textContent ?? '';
            inertScript.replaceWith(script);
        });

        return () => {
            container.innerHTML = '';
        };
    }, [html]);

    return (
        <div
            ref={containerRef}
            className="w-full"
        />
    );
};

export default CompetitionHtmlEmbed;
